import React from 'react'
import PropTypes from 'prop-types'
import { FormattedDate } from 'react-intl'
import { css } from 'glamor'
import moment from 'moment'

import { isKey } from '../../utils'
import { useTheme } from '../../theme'

import getStyles from './styles'

type CalendarDateVariant =
  | 'default'
  | 'cta'
  | 'primary'
  | 'secondary'
  | 'positive'
  | 'negative'
  | 'alert'
  | 'brand'
  | 'white'
  | 'dim'

const variants: CalendarDateVariant[] = [
  'default',
  'cta',
  'primary',
  'secondary',
  'positive',
  'negative',
  'alert',
  'brand',
  'white',
  'dim',
]

interface ICalendarDateProps {
  value: string
  variant?: CalendarDateVariant
}

export function CalendarDate({ value, variant }: ICalendarDateProps) {
  const styles = getStyles(useTheme())

  return (
    <span {...css(styles.root.base)}>
      <span {...css(styles.head.base, isKey(styles.head, variant) && styles.head[variant])}>
        <FormattedDate month="short" value={value} />
      </span>
      <span {...css(styles.body.base)}>
        <FormattedDate day="numeric" value={value} />
      </span>
    </span>
  )
}

CalendarDate.propTypes = {
  variant: PropTypes.oneOf(variants),
  value: PropTypes.string.isRequired,
}

CalendarDate.defaultProps = {
  variant: 'default' as CalendarDateVariant,
}

CalendarDate.VARIANTS = variants

interface ICalendarDateRangeProps {
  from: string
  to: string
  variant?: CalendarDateVariant
}

export function CalendarDateRange({ from, to, variant }: ICalendarDateRangeProps) {
  const styles = getStyles(useTheme())
  const isSameMonth = moment(from).isSame(to, 'month')

  return (
    <span {...css(styles.root.base)}>
      <span
        {...css(
          styles.head.base,
          !isSameMonth && styles.head.range,
          isKey(styles.head, variant) && styles.head[variant]
        )}
      >
        {isSameMonth ? (
          <FormattedDate month="short" value={to} />
        ) : (
          <>
            <FormattedDate month="short" value={from} />
            {' – '}
            <FormattedDate month="short" value={to} />
          </>
        )}
      </span>
      <span {...css(styles.body.base, styles.body.range, isKey(styles.body, variant) && styles.body[variant])}>
        <FormattedDate day="numeric" value={from} />
        {' – '}
        <FormattedDate day="numeric" value={to} />
      </span>
    </span>
  )
}

CalendarDateRange.propTypes = {
  variant: PropTypes.oneOf(variants),
  from: PropTypes.string.isRequired,
  to: PropTypes.string.isRequired,
}

CalendarDateRange.defaultProps = {
  variant: 'default' as CalendarDateVariant,
}

CalendarDateRange.VARIANTS = variants
